import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import ScrollToTop from '@/components/ScrollToTop';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/hooks/useAuth';
import { RequireAuth } from '@/components/RequireAuth';

import Index from './pages/Index';
import CoBrain from './pages/CoBrain';
import Services from './pages/Services';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import EngineeringTerms from './pages/EngineeringTerms';
import VelocityPlans from './pages/VelocityPlans';
import CookiePolicy from './pages/CookiePolicy';
import Status from './pages/Status';
import NotFound from './pages/NotFound';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import ReferralLand from './pages/ReferralLand';
import Ambassadors from './pages/ambassadors/Ambassadors';
import Unsubscribe from './pages/Unsubscribe';
import AmbassadorTerms from './pages/ambassadors/AmbassadorTerms';
import AmbassadorPlaybook from './pages/ambassadors/AmbassadorPlaybook';
import AmbassadorApply from './pages/ambassadors/AmbassadorApply';
import AmbassadorDashboard from './pages/ambassadors/AmbassadorDashboard';
import AmbassadorCircle from './pages/ambassadors/AmbassadorCircle';
import AmbassadorAdmin from './pages/ambassadors/AmbassadorAdmin';
import AmbassadorPayouts from './pages/ambassadors/AmbassadorPayouts';
import { Navigate } from 'react-router-dom';
import { IntercomBoot } from '@/components/IntercomBoot';
import SeoLanding from '@/seo/SeoLanding';
import { SEO_PAGES } from '@/seo/pages';
import { CallMeNowModal } from '@/components/landing/CallMeNowModal';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" storageKey="es-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <Toaster />
            <IntercomBoot />
            <CallMeNowModal />
            <ErrorBoundary>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Index />} />
                {/*
                  LOVABLE-ONLY CRITICAL ROUTES — DO NOT REMOVE.
                  /cobrain and /co-brain are production CoBrain product routes
                  served only from this Lovable project. They are intentionally
                  not present in some GitHub branches; any sync from GitHub main
                  MUST preserve both routes and the CoBrain checkout branch in
                  src/lib/startCheckout.ts. Removal breaks production revenue.
                */}
                <Route path="/cobrain" element={<CoBrain />} />
                <Route path="/co-brain" element={<CoBrain />} />
                <Route path="/services" element={<Services />} />
                <Route path="/contact" element={<Navigate to="/" replace />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/engineering-terms" element={<EngineeringTerms />} />
                <Route path="/velocity-plans" element={<VelocityPlans />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/status" element={<Status />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/r/:code" element={<ReferralLand />} />
                <Route path="/ambassadors" element={<Ambassadors />} />
                <Route path="/ambassadors/terms" element={<AmbassadorTerms />} />
                <Route path="/ambassadors/playbook" element={<AmbassadorPlaybook />} />
                <Route path="/ambassadors/apply" element={<RequireAuth><AmbassadorApply /></RequireAuth>} />
                <Route path="/ambassadors/dashboard" element={<RequireAuth><AmbassadorDashboard /></RequireAuth>} />
                <Route path="/ambassadors/circle" element={<RequireAuth><AmbassadorCircle /></RequireAuth>} />
                <Route path="/ambassadors/admin" element={<RequireAuth><AmbassadorAdmin /></RequireAuth>} />
                <Route path="/ambassadors/admin/payouts" element={<RequireAuth><AmbassadorPayouts /></RequireAuth>} />
                {SEO_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding page={p} />} />
                ))}
                {SEO_PAGES.map((p) => (
                  <Route key={`${p.slug}.html`} path={`/${p.slug}.html`} element={<SeoLanding page={p} />} />
                ))}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
